import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Brain, Plug, Shield, Lock, Globe, ArrowRight, Check, Sparkles, Zap, Crown, Star, ChevronRight, BarChart3, Activity } from 'lucide-react';
import logoImg from '@/assets/logo.png';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCountUp } from '@/hooks/useCountUp';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import Navbar from '@/components/layout/Navbar';

// TODO: replace with real Stripe price IDs from your Stripe dashboard
const STRIPE_PRICE_IDS: Record<string, string> = {
  pro: 'price_xxx_pro',
  quant: 'price_xxx_quant',
};

interface LandingPageProps {
  onAuthClick: () => void;
}

/* ───────── count-up stat component ───────── */
const CountStat = ({ end, prefix = '', suffix = '', label, decimals = 0 }: {
  end: number; prefix?: string; suffix?: string; label: string; decimals?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const value = useCountUp({ end: inView ? end : 0, duration: 2200, decimals, prefix, suffix });
  return (
    <div ref={ref} className="text-center px-6 py-4">
      <p className="text-2xl md:text-3xl font-bold font-mono-data text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">{label}</p>
    </div>
  );
};

/* ───────── pricing data ───────── */
const tiers = [
  {
    id: 'free' as const, name: 'Free', price: '$0', period: 'forever', icon: Zap,
    description: 'Learn & Practice',
    features: ['Demo trading only', 'Up to 2 strategy instances', 'AI signals (view only)', 'Basic market data', 'Community support'],
    cta: 'Start Free', highlight: false,
  },
  {
    id: 'pro' as const, name: 'Pro', price: '$29', period: '/month', icon: Sparkles,
    description: 'Trade Seriously',
    features: ['Live trading enabled', 'Up to 3 broker connections', 'Up to 10 strategy instances', 'AI signals: approve & execute', 'Advanced market analytics', 'Email support'],
    cta: 'Upgrade to Pro', highlight: true,
  },
  {
    id: 'quant' as const, name: 'Quant', price: '$79', period: '/month', icon: Crown,
    description: 'Full Automation',
    features: ['Everything in Pro', 'Unlimited brokers & strategies', 'AI auto-execute signals', 'API access (coming soon)', 'Priority support'],
    cta: 'Upgrade to Quant', highlight: false,
  },
];

const testimonials = [
  { name: 'Rahul S.', role: 'Swing Trader', quote: 'AutoTradePlug replaced 3 hours of daily chart analysis. The AI signals are surprisingly accurate.' },
  { name: 'Priya M.', role: 'Algo Enthusiast', quote: 'Connected Zerodha in minutes. The post-mortem analysis on losing trades is brilliant — I actually learn from failures now.' },
  { name: 'Arjun K.', role: 'Portfolio Manager', quote: 'Sharpe tracking, VaR monitoring, circuit breaker — this is institutional-grade for retail price.' },
];

const steps = [
  { icon: Plug, title: 'Connect', desc: 'Link your broker in one click' },
  { icon: BarChart3, title: 'Configure', desc: 'Pick strategies & set risk limits' },
  { icon: Brain, title: 'AI Analyzes', desc: 'Scans 50+ markets every 15 min' },
  { icon: Activity, title: 'Auto-Trades', desc: 'Executes with written reasoning' },
];

const trustBadges = [
  { icon: Lock, text: '256-bit encryption' },
  { icon: Plug, text: '4 brokers' },
  { icon: Globe, text: '50+ markets' },
  { icon: Brain, text: 'AI-powered' },
];

const footerLinks: Array<{ label: string; href?: string; onClick?: () => void }> = [
  { label: 'Product', href: '#features' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Support', onClick: () => { window.location.href = 'mailto:support@autotradeplug.com'; } },
  { label: 'Terms', href: '/terms' },
  { label: 'Privacy', href: '/privacy' },
];

/* ───────── stagger variants ───────── */
const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0, 0, 0.2, 1] as const } },
};

/* ───────── main page ───────── */
const LandingPage = ({ onAuthClick }: LandingPageProps) => {
  const handleUpgrade = async (tierId: string) => {
    if (tierId === 'free') { onAuthClick(); return; }
    const priceId = STRIPE_PRICE_IDS[tierId];
    if (!priceId) {
      toast({ title: 'Plan unavailable', variant: 'destructive' });
      return;
    }
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { onAuthClick(); return; }
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { priceId, userId: user.id },
      });
      if (error || !data?.url) {
        toast({ title: 'Checkout error', description: error?.message ?? 'Could not start checkout', variant: 'destructive' });
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      console.error('checkout invoke failed', err);
      toast({ title: 'Checkout error', variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar onAuthClick={onAuthClick} />

      {/* ── HERO ── */}
      <section className="relative overflow-hidden pt-24 pb-20 md:pt-32 md:pb-28">
        {/* grid pattern */}
        <div className="absolute inset-0 bg-[size:48px_48px] bg-[image:linear-gradient(to_right,hsl(var(--border)/0.25)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border)/0.25)_1px,transparent_1px)]" />
        {/* radial glow */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[hsl(var(--primary)/0.06)] blur-[120px] pointer-events-none" />

        <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-12 items-center">
          {/* left copy */}
          <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-6">
            <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-[56px] font-bold leading-[1.08] tracking-tight">
              The AI That Trades<br />While You Sleep
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base md:text-lg text-muted-foreground max-w-lg leading-relaxed">
              ATP analyzes 50+ markets every 15 minutes, generates high-conviction signals with written reasoning, and executes across 4 brokers. <span className="font-mono-data text-foreground">Sharpe 2.14. 68% win rate.</span>
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                onClick={onAuthClick}
                className="bg-gradient-to-r from-[hsl(var(--accent-blue))] to-[hsl(var(--accent-purple))] hover:shadow-[0_0_30px_hsl(var(--accent-blue)/0.35)] text-primary-foreground text-base px-8 transition-shadow duration-300"
              >
                Start Free <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </motion.div>
            <motion.p variants={fadeUp} className="text-xs text-muted-foreground">
              No credit card required. Demo account with $10,000 virtual capital.
            </motion.p>
            {/* trust badges */}
            <motion.div variants={fadeUp} className="flex flex-wrap gap-4 pt-2">
              {trustBadges.map((b) => (
                <span key={b.text} className="flex items-center gap-1.5 text-[11px] text-muted-foreground uppercase tracking-widest">
                  <b.icon className="h-3.5 w-3.5" /> {b.text}
                </span>
              ))}
            </motion.div>
          </motion.div>

          {/* right mockup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="hidden md:block"
          >
            <div className="rounded-xl border border-border/40 bg-card/80 backdrop-blur-sm p-5 shadow-[0_0_60px_hsl(var(--primary)/0.06)]">
              {/* mock header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs text-muted-foreground">Portfolio Value</p>
                  <p className="text-2xl font-bold font-mono-data text-foreground">$124,580<span className="text-sm text-[hsl(var(--accent-green))]"> +12.4%</span></p>
                </div>
                <Badge variant="outline" className="text-[10px] border-[hsl(var(--accent-green)/0.3)] text-[hsl(var(--accent-green))]">LIVE</Badge>
              </div>
              {/* mock sparkline */}
              <svg viewBox="0 0 200 40" className="w-full h-10 mb-4">
                <polyline fill="none" stroke="hsl(var(--accent-green))" strokeWidth="1.5"
                  points="0,35 15,30 30,32 45,25 60,28 75,20 90,22 105,15 120,18 135,12 150,14 165,8 180,10 200,5" />
                <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--accent-green))" stopOpacity="0.15" />
                  <stop offset="100%" stopColor="hsl(var(--accent-green))" stopOpacity="0" />
                </linearGradient>
                <polygon fill="url(#sg)"
                  points="0,35 15,30 30,32 45,25 60,28 75,20 90,22 105,15 120,18 135,12 150,14 165,8 180,10 200,5 200,40 0,40" />
              </svg>
              {/* mock strategy rows */}
              {[
                { name: 'EMA Crossover', pnl: '+$1,240', color: 'accent-green' },
                { name: 'RSI Reversal', pnl: '+$860', color: 'accent-green' },
                { name: 'Momentum Breakout', pnl: '-$180', color: 'accent-red' },
              ].map((s) => (
                <div key={s.name} className="flex items-center justify-between py-2 border-t border-border/30 text-sm">
                  <span className="text-muted-foreground">{s.name}</span>
                  <span className={`font-mono-data text-[hsl(var(--${s.color}))]`}>{s.pnl}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── LIVE STATS BAR ── */}
      <section className="border-y border-border/30 bg-card/40">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 divide-x divide-border/20">
          <CountStat end={2.4} prefix="$" suffix="M+" label="Traded Volume" decimals={1} />
          <CountStat end={1200} suffix="+" label="Signals Generated" />
          <CountStat end={312} label="Trades Executed" />
          <CountStat end={68.4} suffix="%" label="Win Rate" decimals={1} />
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: '-60px' }} variants={stagger} className="text-center mb-14">
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold mb-3">Built for Serious Traders</motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground max-w-xl mx-auto">Institutional-grade analytics, AI reasoning, and multi-broker execution — in one platform.</motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: '-40px' }} variants={stagger} className="grid md:grid-cols-3 gap-5">
            {[
              {
                icon: Brain, title: 'AI Signal Engine',
                desc: 'Analyzes prices, macro indicators, correlations, and sentiment every 15 minutes. Generates signals with written reasoning.',
                mock: (
                  <div className="mt-4 rounded-lg border border-border/30 bg-background/50 p-3 text-xs">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-2 h-2 rounded-full bg-[hsl(var(--accent-green))]" />
                      <span className="font-mono-data text-[hsl(var(--accent-green))]">BUY EUR/USD</span>
                      <span className="ml-auto text-muted-foreground">78% conf.</span>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">RSI oversold + DXY weakening + macro tailwind</p>
                  </div>
                ),
              },
              {
                icon: Plug, title: 'Multi-Broker Execution',
                desc: 'Connect Zerodha, Dhan, Upstox, AngelOne. One-click deploy. Auto-execute across all connected brokers.',
                mock: (
                  <div className="mt-4 flex gap-2">
                    {['Zerodha', 'Dhan', 'Upstox', 'AngelOne'].map((b) => (
                      <span key={b} className="rounded-md border border-border/30 bg-background/50 px-2.5 py-1.5 text-[10px] text-muted-foreground">{b}</span>
                    ))}
                  </div>
                ),
              },
              {
                icon: Shield, title: 'Portfolio Intelligence',
                desc: 'Sharpe ratio, VaR, circuit breaker, regime detection. Your capital is protected with institutional risk controls.',
                mock: (
                  <div className="mt-4 rounded-lg border border-border/30 bg-background/50 p-3 text-xs">
                    <div className="flex justify-between mb-1">
                      <span className="text-muted-foreground">Health Score</span>
                      <span className="font-mono-data text-[hsl(var(--accent-green))]">87/100</span>
                    </div>
                    <div className="w-full h-1.5 rounded-full bg-border/30">
                      <div className="h-full rounded-full bg-[hsl(var(--accent-green))]" style={{ width: '87%' }} />
                    </div>
                  </div>
                ),
              },
            ].map((f) => (
              <motion.div key={f.title} variants={fadeUp}>
                <Card className="h-full border-border/30 bg-card/60 hover:border-border/50 transition-colors">
                  <CardHeader className="pb-2">
                    <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary)/0.1)] flex items-center justify-center mb-3">
                      <f.icon className="w-5 h-5 text-primary" />
                    </div>
                    <CardTitle className="text-lg">{f.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                    {f.mock}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="py-20 md:py-28 border-t border-border/20">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold mb-3">How It Works</motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground">From connection to execution in four steps.</motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-0 relative">
            {/* connector line (desktop only) */}
            <div className="hidden md:block absolute top-10 left-[12.5%] right-[12.5%] h-px border-t border-dashed border-border/40" />

            {steps.map((s, i) => (
              <motion.div key={s.title} variants={fadeUp} className="relative flex flex-col items-center text-center gap-3">
                <div className="relative z-10 w-20 h-20 rounded-2xl border border-border/40 bg-card/80 flex items-center justify-center">
                  <s.icon className="w-7 h-7 text-primary" />
                </div>
                <span className="text-[10px] font-mono-data text-muted-foreground uppercase tracking-widest">Step {i + 1}</span>
                <p className="font-semibold text-sm">{s.title}</p>
                <p className="text-xs text-muted-foreground max-w-[140px]">{s.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="py-20 md:py-28 border-t border-border/20">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold mb-3">Simple, Transparent Pricing</motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground">Start free. Scale when you're ready.</motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-5">
            {tiers.map((t) => {
              const Icon = t.icon;
              return (
                <motion.div key={t.id} variants={fadeUp}>
                  <Card className={`relative h-full border-border/30 bg-card/60 transition-all ${t.highlight ? 'ring-2 ring-primary/40 shadow-[0_0_24px_hsl(var(--primary)/0.1)]' : ''}`}>
                    {t.highlight && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge className="bg-primary text-primary-foreground text-[10px] px-3">Most Popular</Badge>
                      </div>
                    )}
                    <CardHeader className="text-center pb-2">
                      <div className="w-10 h-10 mx-auto rounded-xl bg-[hsl(var(--primary)/0.1)] flex items-center justify-center mb-2">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <CardTitle className="text-lg">{t.name}</CardTitle>
                      <p className="text-xs text-muted-foreground">{t.description}</p>
                      <div className="flex items-baseline justify-center gap-1 mt-2">
                        <span className="text-3xl font-bold font-mono-data">{t.price}</span>
                        <span className="text-sm text-muted-foreground">{t.period}</span>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <ul className="space-y-2.5">
                        {t.features.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-sm">
                            <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                            <span className="text-muted-foreground">{f}</span>
                          </li>
                        ))}
                      </ul>
                      <Button
                        className={`w-full ${t.highlight ? 'bg-gradient-to-r from-[hsl(var(--accent-blue))] to-[hsl(var(--accent-purple))] hover:shadow-[0_0_20px_hsl(var(--accent-blue)/0.3)] text-primary-foreground' : ''}`}
                        variant={t.highlight ? 'default' : 'secondary'}
                        onClick={() => handleUpgrade(t.id)}
                      >
                        {t.cta}
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ── SOCIAL PROOF ── */}
      <section className="py-20 md:py-28 border-t border-border/20">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold mb-3">Join 500+ Traders Using AutoTradePlug</motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground">Real traders. Real results.</motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-5">
            {testimonials.map((t) => (
              <motion.div key={t.name} variants={fadeUp}>
                <Card className="h-full border-border/30 bg-card/60">
                  <CardContent className="pt-6">
                    <div className="flex gap-0.5 mb-3">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} className="w-3.5 h-3.5 fill-[hsl(var(--accent-amber))] text-[hsl(var(--accent-amber))]" />
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4">"{t.quote}"</p>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[hsl(var(--primary)/0.15)] flex items-center justify-center text-xs font-bold text-primary">
                        {t.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{t.name}</p>
                        <p className="text-xs text-muted-foreground">{t.role}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="py-16 md:py-20 border-t border-border/20">
        <div className="max-w-3xl mx-auto px-4 text-center space-y-5">
          <h2 className="text-2xl md:text-3xl font-bold">Ready to Automate Your Trading?</h2>
          <p className="text-muted-foreground">Start with a free demo account. No credit card required.</p>
          <Button
            size="lg"
            onClick={onAuthClick}
            className="bg-gradient-to-r from-[hsl(var(--accent-blue))] to-[hsl(var(--accent-purple))] hover:shadow-[0_0_30px_hsl(var(--accent-blue)/0.35)] text-primary-foreground text-base px-8 transition-shadow duration-300"
          >
            Get Started Free <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="border-t border-border/20 py-10">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img src={logoImg} alt="AutoTradePlug logo" className="w-6 h-6 object-contain" />
              <span className="font-bold text-sm">AutoTradePlug</span>
            </div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
              {footerLinks.map((l) => (
                l.onClick ? (
                  <button
                    key={l.label}
                    type="button"
                    onClick={l.onClick}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {l.label}
                  </button>
                ) : (
                  <a key={l.label} href={l.href} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                    {l.label}
                  </a>
                )
              ))}
            </div>
          </div>
          <div className="mt-6 text-center">
            <p className="text-[11px] text-muted-foreground">Proprietary trading engine. Built in-house.</p>
            <p className="text-[10px] text-muted-foreground mt-1">© {new Date().getFullYear()} AutoTradePlug Technologies Pvt Ltd. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
